import * as Sentry from '@sentry/react'

import { getRelease } from '@/lib/env'

import { classifyEvent } from './classifyEvent'

/**
 * Strip the invite-link token from any /invite URL before it reaches Sentry
 * breadcrumbs, replay, or event request data. The token lives in the URL
 * fragment (#token=…) on first load; the page strips it on mount, but the
 * initial navigation breadcrumb can still capture it. This collapses any
 * /invite URL to a bare "/invite", dropping the query + fragment (and thus the
 * token) so it is never sent to Sentry. The B2B invitation page, /work/invite#<code>,
 * keeps its code in the address bar, so it is collapsed to "/work/invite" the same way.
 */
export function scrubInviteUrl(url: string | undefined): string | undefined {
  if (!url) return url
  // Match an absolute or relative URL whose path is /invite or /work/invite, with any
  // query/hash. Anchored on the route boundary: /invite must be followed by ?, #, /,
  // or the end of the URL, so a contrived path like /invitelong#token=… is never matched.
  return url.replace(/(^|\/\/[^/]+)(\/work)?\/invite(?=[?#/]|$)(?:[?#][^\s]*)?/g, '$1$2/invite')
}

/**
 * Single source of truth for Sentry in the frontend. All SDK options, the
 * Session Replay policy, breadcrumb scrubbing, and the {@link classifyEvent}
 * classifier are wired here. Call once from `main.tsx` before any provider or
 * React Query hook fires.
 */
export function initSentry(): void {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENV,
    release: getRelease(),
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 1.0,

    // Session Replay — the single control point for replay volume.
    // `replaysSessionSampleRate`: fraction of ALL sessions recorded start-to-end.
    // `replaysOnErrorSampleRate`: fraction of error sessions whose buffered
    // replay is uploaded. NOTE: this is a per-SESSION, uniform sample. The
    // installed SDK (v10) has no per-event replay gate, and the on-error flush
    // keys on event TYPE (not level), so a 4xx downgraded to `warning` still
    // uploads a replay when its session is sampled. Tune the rate HERE — this is
    // the only lever short of a manual buffer-mode + selective flush rework.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    beforeBreadcrumb(breadcrumb) {
      // Navigation/fetch/xhr breadcrumbs carry URLs that can include the invite
      // token (most importantly the very first navigation to /invite#token=…).
      if (breadcrumb.data) {
        if (typeof breadcrumb.data.from === 'string') breadcrumb.data.from = scrubInviteUrl(breadcrumb.data.from)
        if (typeof breadcrumb.data.to === 'string') breadcrumb.data.to = scrubInviteUrl(breadcrumb.data.to)
        if (typeof breadcrumb.data.url === 'string') breadcrumb.data.url = scrubInviteUrl(breadcrumb.data.url)
      }
      return breadcrumb
    },

    beforeSend(event, hint) {
      // Scrub the invite token from the event's request URL before classifying.
      if (event.request?.url) {
        event.request.url = scrubInviteUrl(event.request.url)
      }
      return classifyEvent(event, hint)
    },
  })
}
